import React, { useRef, useState } from "react";
import { Upload, FileText, ImageIcon, Loader2, ScanLine, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function UploadCard({ onFile, loading, onLoadSample, info }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleFiles = (files) => {
    if (!files?.[0]) return;
    setFileName(files[0].name);
    onFile(files[0]);
  };

  return (
    <Card className="p-5 sm:p-6 border-slate-200">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold tracking-tight">Biometry Scan Upload</h2>
          <p className="text-sm text-slate-500 mt-1">
            Drop your IOLMaster / Lenstar / Argos / Pentacam printout — AI extracts every value automatically.
          </p>
        </div>
        <div className="hidden sm:block text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Step 01
        </div>
      </div>

      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed transition-all
          ${drag ? "border-sky-500 bg-sky-50" : "border-slate-300 bg-slate-50/60 hover:bg-sky-50/40 hover:border-sky-400"}
          px-6 py-10 flex flex-col items-center justify-center text-center`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {loading ? (
          <>
            <Loader2 className="w-8 h-8 text-sky-700 animate-spin" />
            <div className="mt-3 text-sm font-medium text-sky-800">Gemini vision is reading the scan…</div>
            <div className="mt-1 text-xs text-slate-500">This usually takes 5–15 seconds.</div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-white grid place-items-center border border-slate-200 shadow-sm">
              <Upload className="w-5 h-5 text-sky-700" />
            </div>
            <div className="mt-3 font-medium">
              {fileName ? (
                <span className="text-sky-800">{fileName}</span>
              ) : (
                <>Drop biometry scan here, or click to browse</>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              PDF · JPG · PNG · up to 20 MB · eyes are identified by their OD / OS labels on the printout
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> PDF</span>
              <span className="inline-flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> JPG / PNG</span>
            </div>
          </>
        )}
      </div>

      <ExtractSummary info={info} />

      <div className="mt-4 flex flex-wrap gap-2">
        <span className="text-xs text-slate-500 self-center mr-2">or try a preset:</span>
        <Button size="sm" variant="outline" data-testid="preset-normal" onClick={() => onLoadSample("normal")}>Normal eye</Button>
        <Button size="sm" variant="outline" data-testid="preset-aniso" onClick={() => onLoadSample("anisometropia")}>AL anisometropia</Button>
        <Button size="sm" variant="outline" data-testid="preset-post-lasik" onClick={() => onLoadSample("postLasik")}>Post-LASIK</Button>
      </div>
    </Card>
  );
}

function ExtractSummary({ info }) {
  if (!info) return null;
  return (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 p-3 text-xs" data-testid="extract-summary">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-700">
            <span className="inline-flex items-center gap-1.5 font-semibold text-sky-900">
              <ScanLine className="w-3.5 h-3.5" /> {info.device || "Biometry printout"}
            </span>
            {info.pages > 1 && <span className="text-slate-500">{info.pages} pages read</span>}
            <span className="text-slate-500">Eyes: <span className="font-mono text-slate-900">{info.eyes.join(" + ") || "—"}</span></span>
            {info.confidence.od != null && (
              <span className="text-slate-500">OD confidence <span className="font-mono text-slate-900" data-testid="extract-conf-od">{Math.round(info.confidence.od * 100)}%</span></span>
            )}
            {info.confidence.os != null && (
              <span className="text-slate-500">OS confidence <span className="font-mono text-slate-900" data-testid="extract-conf-os">{Math.round(info.confidence.os * 100)}%</span></span>
            )}
          </div>
          {info.warnings.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="extract-warnings">
              {info.warnings.map((w, i) => (
                <li key={`${i}-${w}`} className="flex items-start gap-1.5 text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{w}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
  );
}
